/**
 * PartnerSection
 *
 * The /partner page — single surface for all partner status, invitation, and
 * management actions. Reachable via the sidebar profile popup's "Partner" /
 * "Link partner" row regardless of link state.
 *
 * Modeled on AccountSettingsSection: same list-page shell (sticky
 * .page-header__title + subtitle row over an .explore-view--list scroll
 * container) wrapping the shared .settings-column, same .account-section +
 * .card .account-section__card structure, .account-row body/action layout,
 * and inline-expansion confirmation pattern.
 *
 * --- Top-level page states ---
 *   loading          initial fetch (linked check + invitation create-or-get)
 *   error            fetch failed (with retry)
 *   linked           partner attached → identity card + Sharing card. When the
 *                    viewer's Premium carries the partner rate the identity
 *                    card carries a "Partner rate" chip and the disconnect
 *                    confirm states the price consequence; when it doesn't
 *                    (and the rate is configured), a dismissible banner
 *                    pitches it.
 *   active           unlinked + active invitation → invite + share + privacy + manage
 *   cancelled        unlinked + invitation just revoked → status + recreate CTA + privacy
 *
 * --- Inline substates (within `linked`) ---
 *   confirmingDisconnect / disconnecting   inline confirm row, type-token gate
 *
 * --- Inline substates (within `active`) ---
 *   inviteChannel                          'link' | 'email' — which channel the
 *                                          invite card is showing
 *   sendingEmail                           submitting email
 *   copied                                 link just copied (2s revert)
 *   confirmingCancel / cancellingInvite    inline confirm + DELETE in flight
 *   recreating                             POST to mint a fresh invitation
 *
 * The unlinked state absorbs everything PartnerInvitationModal used to do
 * (the modal has been deleted). One card whose body swaps between the two
 * invitation channels, with the segmented control as the persistent landmark:
 *
 *   ┌─────────────────────────────────────────┐
 *   │ Invite your partner                     │
 *   │ They'll make their own account. …       │  ← framing the bare URL lacked
 *   │        [ 🔗 Link │ ✉ Email ]            │  ← always visible, shows where
 *   │                                         │    you are + how to get back
 *   │ ┌─────────────────────────────────────┐ │
 *   │ │ 🔗 https://encouple.app/join/…  Copy│ │  ← inset field, one primary
 *   │ └─────────────────────────────────────┘ │
 *   │ Paste it into a text, chat, or DM.      │  ← helper slot, always present
 *   └─────────────────────────────────────────┘    so the swap can't jump
 *   │           Cancel invitation             │  ← subtle footer link
 *   │                                         │
 *   │ ─── Common questions ─────              │  ← shared .faq component
 *   │ ▸ What if my partner doesn't accept?    │
 *   │ ▸ What can my partner see?  (privacy)   │
 *   │ ▸ Can I link with someone …             │
 *   └─────────────────────────────────────────┘
 *
 * After cancel, the cancelled state simplifies to a single recreate CTA:
 *
 *   ┌─────────────────────────────────────────┐
 *   │ Header: "Invite your partner"           │
 *   │  Invitation cancelled  [ Create new ]   │
 *   └─────────────────────────────────────────┘
 *
 * Backend routes consumed:
 *   - GET    /api/discovery/couples/partner                      (link status)
 *   - POST   /api/discovery/couples/invitations                  (idempotent create-or-get)
 *   - POST   /api/discovery/couples/invitations/:id/send-email   (send/resend)
 *   - DELETE /api/discovery/couples/invitations/active           (revoke)
 *   - POST   /api/discovery/couples/dissolve                     (unlink)
 *   - GET    /api/discovery/couples/sharing-status               (the Sharing view model)
 *   - PATCH  /api/discovery/couples/sharing/unit/:unitId         (one unit's Full/Basic/Off)
 *   - POST   /api/discovery/couples/sharing/submit               (the queue's atomic batch: all unit states + confirm)
 *   - PATCH  /api/discovery/couples/sharing/master               (the single Partner Sharing switch)
 *   - GET    /api/subscription/status  (via the shared snapshot — partnerRate)
 *   - GET    /api/subscription/config  (is the partner rate offered at all?)
 *
 * --- Page shell ---
 * The /library shell — centered sticky `page-header__title` — plus, in the
 * `linked` state, a `.page-toggle` pill sub-nav (`_renderTabNav`, shared with
 * /notes, /discovery and /library) splitting the body into two sub-pages:
 *   - Overview: one Account card — identity + disconnect, the single Partner
 *     Sharing master switch, and the first-feed notice. Nothing per-area, and
 *     nothing gate-related beyond the one-line locked/generating/error note,
 *     lives here.
 *   - Sharing: the consent registry (`partnerSharing/SharingRegistry.js`) —
 *     one row per share unit and its detail panel (the pre-filled overview
 *     with its "Approve & share", the optional one-by-one walk, and the
 *     single-topic detail). This is where every per-unit consent decision
 *     is made.
 * The registry renders whenever the sharing-status read returns share units;
 * states with no ledger yet (locked / generating / error) fall back to a
 * notice on the Sharing pill — for `locked` specifically, that notice also
 * carries the two-person foundations gate strip (avatars, bars, the
 * "Continue …" CTA) so the pill dedicated to sharing is also where the fix
 * for "you can't share yet" lives.
 * Unlinked/cancelled/loading/error states have no pills — the invite flow
 * (or loading/error placeholder) owns the whole page.
 */

import page from '../vendor/page.js';
import { DiscoveryAPI } from '../core/discoveryApi.js';
import { SubscriptionAPI } from '../core/subscriptionApi.js';
import {
  fetchSubscriptionStatus,
  invalidateSubscriptionStatus,
  partnerRateFromStatus
} from '../core/subscriptionStatus.js';
import { eventBus, escapeHtml } from '../core/utils.js';
import { createProfileAvatar } from '../utils/profileAvatar.js';
import { goToUpgrade } from '../utils/upgradeNavigation.js';
import { renderSwitchRow } from '../components/uiSwitch.js';
import { StickyStack } from '../utils/StickyStack.js';
import { Tooltip } from '../utils/Tooltip.js';
import {
  buildPageInfoPanel,
  renderPageInfoTitle,
  renderPageInfoFlow,
  renderPageInfoFaq
} from '../components/pageInfoPanel.js';
import { SharingRegistry } from './partnerSharing/SharingRegistry.js';
import { ledgerFromStatus, ledgerFromSubmitResponse, queueScope } from './partnerSharing/sharingLedgerModel.js';
import { PRIVATE_ROWS } from '../components/sharingConsentPrimitives.js';

/**
 * Token the user must type verbatim to enable the disconnect button. Kept
 * uppercase so it stands out and resists accidental keystrokes. Cancel-
 * invitation does NOT require a typed token (it's recoverable — they can
 * just create a new invitation).
 */
const DISCONNECT_TOKEN = 'DISCONNECT';

/**
 * localStorage key for the partner-rate upsell banner's dismissal. Plain
 * boolean flag rather than UpgradeService's component-state blob: this banner
 * has no re-show window — dismissed is dismissed.
 */
const UPSELL_DISMISS_KEY = 'partner_rate_upsell_dismissed';

/** Partner-rate monthly price shown when `/subscription/config` omits it. */
const PARTNER_RATE_MONTHLY_FALLBACK = 15;

/** Premium list price shown when the status payload carries no `amount`. */
const PREMIUM_MONTHLY_FALLBACK = 20;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The two states that resolve on their own, and so are lightly polled until
 * they move on. Bounded so neither ever loops forever (~1 min ceiling each).
 *
 * - `generating`: the registers are still being built.
 * - `waiting`: this user has confirmed and is waiting on their partner — but it
 *   is ALSO where a pair who confirmed simultaneously lands with no build
 *   enqueued. `GET /sharing-status` carries the recovery read that enqueues it,
 *   so polling is what heals that pair; without it the page sits on `waiting`
 *   until someone reloads.
 */
const SHARING_POLL_STATES = new Set(['generating', 'waiting']);
const SHARING_POLL_INTERVAL_MS = 4000;
const SHARING_POLL_MAX = 15;

/**
 * Shared side of the visibility split shown both in the linked-state "What
 * {Name} sees" card (placeholder toggles) and the unlinked-state privacy
 * preview (informational lists matching the consent screen). The private
 * side (`PRIVATE_ROWS`) lives in `sharingConsentPrimitives.js`, kept apart
 * so the promise has one source even though this preview is its only
 * renderer today.
 */
const SHARED_ROWS = [
  'Discovery path progress',
  'Approved partner notes',
  'Some core assessment results'
];

/**
 * Inline SVG icon strings for the share-action buttons. Inlined (rather
 * than via the sprite) because the sprite has copy/share but lacks mail/
 * link/send/spinner — keeping all six in one place avoids a mixed-source
 * inconsistency. Each is sized 16×16 to sit cleanly inside .account-btn.
 */
const ICONS = {
  link:  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 1 0-5.66-5.66l-1.5 1.5M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 1 0 5.66 5.66l1.5-1.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>',
  copy:  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" stroke-width="1.75" fill="none"/><path d="M5 15V6a2 2 0 0 1 2-2h9" stroke="currentColor" stroke-width="1.75" fill="none" stroke-linecap="round"/></svg>',
  share: '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><circle cx="6" cy="12" r="2.5" stroke="currentColor" stroke-width="1.75" fill="none"/><circle cx="17" cy="6" r="2.5" stroke="currentColor" stroke-width="1.75" fill="none"/><circle cx="17" cy="18" r="2.5" stroke="currentColor" stroke-width="1.75" fill="none"/><path d="M8.5 11L15 7M8.5 13L15 17" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" fill="none"/></svg>',
  mail:  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" stroke-width="1.75" fill="none"/><path d="M3 7l9 6 9-6" stroke="currentColor" stroke-width="1.75" fill="none" stroke-linecap="round"/></svg>',
  send:  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M21 3L3 10l7 3 3 7 8-17z" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round" fill="none"/></svg>',
  check: '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M5 12l4 4 10-10" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>'
};

/**
 * The "Shared" / "Stays private" two-column list, built from `SHARED_ROWS`
 * and `PRIVATE_ROWS`. Shared between the invite-flow FAQ's privacy answer
 * and the page-info panel's "What's shared" tab, so both surfaces read the
 * same canonical lists rather than each hard-coding a copy.
 * @returns {string} The two `.partner-privacy__col` blocks (caller supplies
 *   the wrapping `.partner-privacy` grid and any lead-in copy)
 */
function renderSharedPrivateColumns() {
  return `
    <div class="partner-privacy__col">
      <h4 class="partner-privacy__heading">Shared</h4>
      <ul class="partner-privacy__list">
        ${SHARED_ROWS.map(item => `<li>${item}</li>`).join('')}
      </ul>
    </div>
    <div class="partner-privacy__col">
      <h4 class="partner-privacy__heading">Stays private</h4>
      <ul class="partner-privacy__list">
        ${PRIVATE_ROWS.map(item => `<li>${item}</li>`).join('')}
      </ul>
    </div>
  `;
}

/**
 * FAQ items rendered below the invite card. Privacy info lives here (Q2)
 * rather than getting its own page section. Items can carry either a
 * plain-text `a` (rendered inside a <p>) or `aHtml` (raw HTML) — used by
 * the privacy answer to render a two-column shared / private split.
 */
const FAQ_ITEMS = [
  {
    q: 'What if my partner doesn\u2019t accept?',
    a: 'Nothing changes \u2014 your account works the same. The link stays active until you cancel it. You can resend or share it again any time.'
  },
  {
    q: 'What can my partner see, and what stays private?',
    aHtml: `
      <div class="partner-privacy">
        <p class="partner-privacy__intro">
          Nothing reaches your partner without your approval — and even then, only
          high-level themes and core traits. The details of your conversations stay yours.
        </p>
        ${renderSharedPrivateColumns()}
      </div>
    `
  },
  {
    q: 'Can I link with someone who isn\u2019t a romantic partner?',
    a: 'Yes \u2014 encouple is designed for couples but works for any two people who want a shared lens for their relationship: friends, family members, co-parents, or anyone else.'
  },
  {
    q: 'What happens if we disconnect later?',
    a: 'You\u2019ll both keep your individual data. Shared progress stops updating immediately, and joint context from the linked period doesn\u2019t carry over if you reconnect. If you were on the partner rate, Premium goes back to the full price from your next bill.'
  }
];

/**
 * The single Partner Sharing master switch (owner call 2026-08-28: the two
 * per-feature masters consolidated into one), on the settings-row register:
 * a short title + one high-level sentence (owner call, same day): the benefit
 * (better guidance for the couple) + the guarantee (only approved information
 * ever crosses). The mechanics — notes feed, registry depths — live on the
 * Sharing pill, not in this hint. `hint` stays a function for register parity
 * with the other settings rows, though this copy no longer names the partner.
 */
const SHARING_COPY = {
  master: {
    title: 'Partner Sharing',
    hint: () => 'Improves guidance for you both — only information you approve is ever shared.'
  }
};

/**
 * Note riding under the Core Psychology row, keyed by sharing state. Only
 * states the user can't act on get one — `ready` and `paused` are both plain
 * off, and the switch says that by itself.
 *
 * `tone` selects a .partner-row__state--* colour, which is the note's entire
 * styling (no box or background): info = working, caution = blocked on a
 * prerequisite, danger = failed.
 */
const SHARING_STATE_NOTES = {
  // The gate is per-partner (GATE_PATH_IDS in routes/discoveryCouples.js): `locked`
  // only ever means the VIEWER hasn't finished both Foundations paths — a partner
  // who is behind no longer blocks this user's setup. Both paths are named, not
  // just the first: naming only "Starting Your Journey" left users who had
  // finished exactly that still locked out with no explanation.
  locked: { text: 'Notes sharing is active now. Assessment sharing unlocks once you’ve finished Starting Your Journey and Continuing Your Journey.', tone: 'caution' },
  generating: { text: 'Preparing your shared understanding…', tone: 'info' },
  error: { text: 'Something went wrong preparing this. Try again in a moment.', tone: 'danger' }
};

/**
 * Page title + subtitle, keyed by top-level state.
 *
 * The unlinked states own the page header outright: there is no partner yet,
 * so "Partner" names nothing and "what you share" describes a state the user
 * isn't in. The header carries the whole framing instead — what the page is
 * for, and what happens to the person who accepts — which is what the invite
 * UI below it can then stop repeating.
 *
 * The linked title stays the single word agreed for the profile-menu pages
 * (Account / Help / Partner / Memory).
 */
const PAGE_HEADINGS = {
  active: {
    title: 'Invite A Partner',
    subtitle: 'They’ll make their own account. Nothing you’ve written is shared until you choose to share it.'
  },
  cancelled: {
    title: 'Invite A Partner',
    subtitle: 'They’ll make their own account. Nothing you’ve written is shared until you choose to share it.'
  }
};
const PAGE_HEADING_DEFAULT = {
  title: 'Partner',
  subtitle: 'Your connection and what you share'
};

/**
 * Whether the partner-rate upsell banner has been dismissed. Storage failures
 * (private mode, quota) read as "not dismissed" — the banner is dismissible
 * again, which is friendlier than suppressing it on an unreadable store.
 *
 * @returns {boolean} True when previously dismissed
 */
function readUpsellDismissed() {
  try {
    return localStorage.getItem(UPSELL_DISMISS_KEY) === '1';
  } catch (_err) {
    return false;
  }
}

/**
 * Format an ISO date string as "April 19, 2026". Empty string on bad input.
 */
function formatLongDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

export class PartnerSection {
  /**
   * @param {Object} elements
   * @param {HTMLElement} elements.container - The #partner-page div
   * @param {EventBus} [eventBus]
   */
  constructor(elements, eventBus) {
    this.container = elements.container;
    this.eventBus = eventBus;
    this.discoveryApi = new DiscoveryAPI();
    this.isActive = false;

    /** @type {'loading'|'linked'|'active'|'cancelled'|'error'} */
    this.state = 'loading';
    this.partner = null;
    this.invitation = null;
    this.errorMessage = '';

    /**
     * Pill sub-page within the `linked` state. 'overview' = identity card +
     * the two master switches; 'sharing' = the consent registry (plus, while
     * locked, the foundations gate strip), where every per-unit decision is
     * made.
     * @type {'overview'|'sharing'}
     */
    this.currentTab = 'overview';

    // Linked-state inline UI flags
    this.confirmingDisconnect = false;
    this.disconnecting = false;
    this.disconnectConfirmText = '';

    // Unlinked-state inline UI flags
    /**
     * @type {'link'|'email'} Which invitation channel the card body is showing.
     * Defaults to 'link' — Copy is the lower-commitment action, and landing on
     * a focused email field would presume the user's channel for them.
     */
    this.inviteChannel = 'link';
    this.sendingEmail = false;
    this.emailError = '';
    this.copied = false;
    this._copyRevertTimer = null;
    this.confirmingCancelInvite = false;
    this.cancellingInvite = false;
    this.recreating = false;

    // ----- Sharing (masters on Overview, consent ledger on the Sharing pill) -----
    /** @type {'idle'|'loading'|'loaded'|'unavailable'|'error'} */
    this.sharingLoadStatus = 'idle';
    /** @type {Object|null} The Sharing view model from getSharingStatus. */
    this.sharing = null;
    this.sharingError = '';
    /** One-shot `?share=1` entry intent: scroll the sharing surface into view. */
    this._scrollToSharing = false;
    /** One-shot `?queue=<groupId>` entry intent (the dashboard nudge's deep
     *  link — design §3.2): auto-open the ceremony queue for that group. */
    this._queueIntent = null;

    // ----- Partner-rate context (chip, unlink consequence, upsell banner) -----
    /** @type {Object|null} Shared subscription snapshot (see core/subscriptionStatus.js). */
    this.subscriptionStatus = null;
    /** @type {{available: boolean, amount: number}|null} `partnerRate` block from /subscription/config, or null. */
    this.partnerRateConfig = null;
    /** @type {boolean} Whether the upsell banner was dismissed (persisted). */
    this._upsellDismissed = readUpsellDismissed();

    /** Guards concurrent memo-feed / notice PATCHes so races can't interleave. */
    this._sharingBusy = false;
    /** Guards the discovery master switch while its PATCH is in flight. */
    this._sharingSubmitting = false;
    this._sharingPollTimer = null;
    this._sharingPollAttempts = 0;
    /** @type {string|null} Which polled state the attempt count belongs to. */
    this._sharingPollState = null;

    /* ----- Sharing registry (the Sharing pill's consent ledger) ----- */
    /**
     * The consent ledger the Sharing registry reads and mutates — derived from
     * the sharing-status payload, or a `structuredClone` of the active
     * fixture's `registry` block under the mock harness. Null until there are
     * share units to show.
     *
     * This section OWNS it: it survives `_switchTab`/`render()` round trips, and
     * mutations land in place so the registry's reference stays valid. The
     * imported fixture module is a page-lifetime singleton and is never mutated.
     * @type {Object|null}
     */
    this.registryLedger = null;
    /** @type {import('./partnerSharing/SharingRegistry.js').SharingRegistry|null} */
    this._sharingRegistry = null;

    /** @type {StickyStack|null} Only two slices exist (title + pill bar), but
     * the pill bar is a second [data-sticky-slice] so it still needs the
     * controller to get a real cumulative offset (see sticky-stack.css). */
    this.stickyStack = null;

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  /* ==================================================================== *
   * The `linked` state's Overview pill: ONE Account card —                *
   *   - Identity: overlapping self+partner avatars, "You & Partner"       *
   *     couple title, linked date (no email — who's who is legible from   *
   *     the avatars/name), + integrated Disconnect (its type-to-confirm   *
   *     accordion opens inside the same card).                            *
   *   - Sharing region below it: the single Partner Sharing master        *
   *     switch and the first-feed notice.                                 *
   * The Sharing pill owns every per-unit consent decision (the registry), *
   * plus — while locked — the two-person foundations gate strip.         *
   * Unlinked/cancelled states hand the whole page to the invite flow      *
   * instead — no pills.                                                   *
   * ==================================================================== */

  _renderPage() {
    const heading = PAGE_HEADINGS[this.state] || PAGE_HEADING_DEFAULT;
    // The invite states' channel toggle is deliberately NOT a sticky slice —
    // there's no long list under it to scroll away, so pinning it would only
    // steal height. The linked state's pill bar (below) IS one, since it's
    // real page-level navigation the user may want pinned while scrolling a
    // long Sharing card.
    return `
      <div class="explore-section">
        <div class="explore-container">
          <div class="explore-view--list" data-sticky-stack>
            ${renderPageInfoTitle(heading.title)}

            <div class="page-header content-width">
              <div class="page-header__subtitle-row">
                <p class="page-header__subtitle partner-page__subtitle">${heading.subtitle}</p>
              </div>
            </div>

            ${this.state === 'linked' ? this._renderTabNav() : ''}

            ${this._renderPageBody()}
          </div>
        </div>
      </div>
      ${this._renderMockSwitcher()}
    `;
  }

  /**
   * Page-toggle pill sub-nav for the `linked` state. Uses the same shared
   * component as /notes, /discovery and /library. It has no counts.
   */
  _renderTabNav() {
    const items = `
      ${this._renderTabNavItem('overview', 'Overview')}
      ${this._renderTabNavItem('sharing', 'Sharing')}
    `;
    return `
      <div class="page-toggle-row content-width" data-sticky-slice data-sticky-collapse>
        <div class="page-toggle" role="tablist" aria-label="Partner page">${items}</div>
      </div>
    `;
  }

  _renderTabNavItem(id, label) {
    const isActive = this.currentTab === id;
    return `
      <button type="button"
              class="page-toggle__btn ${isActive ? 'page-toggle__btn--active' : ''}"
              role="tab" aria-selected="${isActive}"
              data-action="partner-tab" data-tab="${id}">${escapeHtml(label)}</button>`;
  }

  _renderPageBody() {
    if (this.state === 'loading') {
      return `<div class="settings-column">${this._renderLoadingSection()}</div>`;
    }
    if (this.state === 'error') {
      return `<div class="settings-column">${this._renderErrorSection()}</div>`;
    }
    // Not linked → the invite / cancelled flow owns the page (no sharing yet).
    if (this.state === 'cancelled') {
      return `<div class="settings-column">${this._renderCancelledSections()}</div>`;
    }
    if (this.state === 'active') {
      return `
        <div class="settings-column">
          ${this._renderOrientationBackLink()}
          ${this._renderInviteSections()}
        </div>`;
    }
    // Linked → pill-routed: Overview (identity + masters) or the Sharing
    // registry.
    //
    // The consent registry gets a wider, bare shell: `.settings-column` caps at
    // ~700px and imposes its own flex/gap, neither of which can be escaped from
    // inside. Overview and the no-ledger-yet notice keep it.
    if (this._registryActive()) {
      return `
        <div class="content-width partner-sharing-page">
          ${this._renderPartnerRateUpsell()}
          ${this._renderSharingTab()}
        </div>
      `;
    }
    return `
      <div class="settings-column partner-single">
        ${this._renderPartnerRateUpsell()}
        ${this.currentTab === 'sharing' ? this._renderSharingTab() : this._renderOverviewTab()}
      </div>
    `;
  }

  /** Overview pill: the single Account card (identity + the sharing master). */
  _renderOverviewTab() {
    return this._renderAccountBlock();
  }

  /**
   * Sharing pill: the consent registry whenever there are share units to
   * decide. Before that there is nothing to consent to — locked, generating and
   * error each say why (the same one-line reason the Overview switch carries),
   * and everything else falls through to the "controls live here" note.
   *
   * `locked` additionally gets the two-person foundations gate strip
   * (`_renderGateProgress`) right under its note: this is where a user who
   * lands on Sharing to manage assessment consent hits the wall, so this is
   * where the "why, and what to do about it" detail belongs — not on
   * Overview, which only owns the master switch.
   */
  _renderSharingTab() {
    if (this._registryActive()) return this._renderSharingRegistryShell();
    const stateNote = SHARING_STATE_NOTES[this.sharing?.state];
    if (stateNote) {
      const gateProgress = this.sharing?.state === 'locked' ? this._renderGateProgress(this.sharing) : '';
      return `
        <section class="account-section">
          <div class="card account-section__card">
            <p class="account-section__note">${stateNote.text}</p>
            ${gateProgress}
          </div>
        </section>
      `;
    }
    return `
      <section class="account-section">
        <div class="card account-section__card">
          <p class="account-section__note">
            Detailed sharing and approval controls are moving here. For now, manage
            what you share from the Overview tab.
          </p>
          <button type="button" class="account-btn" data-action="partner-tab" data-tab="overview">
            Go to Overview
          </button>
        </div>
      </section>
    `;
  }

  /**
   * The registry's static shell: banner region, toolbar mount, row list. (No
   * lede — the review banner carries the process reassurance, and only while
   * there is actually something to review.)
   * `SharingRegistry` (mounted post-attach by `_mountSharingRegistry()`) fills
   * the three marked sub-containers and never rebuilds this wrapper — that is
   * what keeps the `ResultsFilterBar` instance alive across ordinary updates.
   *
   * @returns {string} HTML
   */
  _renderSharingRegistryShell() {
    return `
      <section class="partner-sharing-registry" data-sharing-registry>
        <div class="partner-sharing-registry__banners" data-sharing-banners></div>
        <div class="partner-sharing-registry__toolbar" data-filter-bar-mount="partner-sharing"></div>
        <div class="session-cards-container partner-sharing-registry__list" data-sharing-rows></div>
      </section>
    `;
  }

  /* ----- Partner-rate surfaces (chip, upsell, unlink consequence) ----- */

  /**
   * Whether the viewer's own Premium currently carries the partner rate. Read
   * from the shared snapshot; a missing block (pre-v3 payload, or none loaded
   * yet) reads as not applied.
   *
   * @returns {boolean} True when the partner rate is applied
   */
  _partnerRateApplied() {
    return partnerRateFromStatus(this.subscriptionStatus).applied;
  }

  /**
   * Small chip beside the couple title, shown only when the viewer's Premium
   * carries the partner rate. Role-agnostic: the rate is symmetric, so there is
   * no payer/member distinction to surface.
   *
   * @returns {string} Chip markup, or empty string
   */
  _renderPlanChip() {
    if (!this._partnerRateApplied()) return '';
    return `
      <span class="account-tier-badge account-tier-badge--premium partner-plan-chip">
        Partner rate
      </span>`;
  }

  /**
   * Dismissible banner pitching the partner rate to a linked pair who don't
   * have it yet. Suppressed when the rate is already applied, when it isn't
   * configured (no rate to pitch), and once dismissed.
   *
   * Deliberately a plain banner, not `UpgradeService.featureGate`: nothing here
   * is gated, and the copy stays neutral — no awareness of beta grants or of
   * what the partner is paying. It is shown to a Premium viewer too: their
   * partner subscribing is what unlocks the rate for both.
   *
   * @returns {string} Banner markup, or empty string
   */
  _renderPartnerRateUpsell() {
    if (this._upsellDismissed) return '';
    if (this._partnerRateApplied()) return '';
    if (!this.partnerRateConfig?.available) return '';

    const amount = this.partnerRateConfig.amount || PARTNER_RATE_MONTHLY_FALLBACK;

    return `
      <section class="partner-upsell" aria-label="Partner rate">
        <p class="partner-upsell__body">
          Premium is $${escapeHtml(String(amount))}/month each when you\u2019re both subscribed.
        </p>
        <div class="partner-upsell__actions">
          <button type="button" class="account-btn" data-action="partner-rate-upsell-view">
            See Premium
          </button>
          <button type="button" class="partner-upsell__dismiss"
                  data-action="partner-rate-upsell-dismiss"
                  aria-label="Dismiss">Not now</button>
        </div>
      </section>
    `;
  }

  /** Persist the upsell dismissal and drop the banner. */
  _dismissPartnerRateUpsell() {
    this._upsellDismissed = true;
    try {
      localStorage.setItem(UPSELL_DISMISS_KEY, '1');
    } catch (err) {
      console.warn('[PartnerSection] Could not persist upsell dismissal:', err?.message || err);
    }
    this.render();
  }

  /**
   * The billing consequence of disconnecting: when the viewer's Premium
   * carries the partner rate, unlinking ends it and their bill returns to the
   * list price from the next invoice. Silent otherwise — nothing about their
   * bill changes. Symmetric by construction: the partner's own rate is their
   * own subscription's business and is never described here.
   *
   * @returns {string} One consequence sentence, or empty string
   */
  _disconnectBillingConsequence() {
    if (!this._partnerRateApplied()) return '';
    const listAmount = this.subscriptionStatus?.amount || PREMIUM_MONTHLY_FALLBACK;
    const nextBill = formatLongDate(this.subscriptionStatus?.currentPeriodEnd);
    const from = nextBill ? `your next bill on ${nextBill}` : 'your next billing date';
    return `Your Premium continues at $${listAmount}/month from ${from}.`;
  }

  /* ----- The Account card: identity + integrated disconnect, with the
   * sharing region (the single Partner Sharing master) below them. ----- */

  _renderAccountBlock() {
    const name = escapeHtml(this.partnerDisplayName());
    // The identity is the couple, not just the partner — "Doug & Laurel" reads
    // as the connection itself. Who's who is legible from the two avatars +
    // name order, so the email line (which did that job before) is dropped.
    const coupleTitle = `${escapeHtml(this._selfDisplayName())} & ${name}`;
    const linkedSince = formatLongDate(this.partner?.linkedAt);
    const metaLine = linkedSince
      ? `<p class="account-profile__meta"><span class="account-profile__meta-item">Linked ${escapeHtml(linkedSince)}</span></p>`
      : '';

    // Disconnect rides the RIGHT of the identity column on wide screens and
    // wraps beneath the meta line on narrow ones — one DOM position, the wrap
    // handled purely by flex in .partner-headcard__info. Wrapping (rather than
    // shrinking) matters because .account-profile__name ellipsizes: a button
    // holding its line would eat the couple title's width on a phone.
    const disconnectBtn = this.confirmingDisconnect ? '' : `
      <button type="button" class="account-btn partner-headcard__disconnect" data-action="disconnect">Disconnect</button>`;
    const errorBlock = (!this.confirmingDisconnect && this.errorMessage)
      ? `<p class="account-form__error" role="alert">${escapeHtml(this.errorMessage)}</p>` : '';

    return `
      <section class="account-section">
        <h2 class="account-section__label">Account</h2>
        <div class="card account-section__card partner-headcard">
          <div class="account-profile">
            <span class="partner-headcard__avatars">
              <span class="partner-headcard__avatar-item" data-profile-avatar="self" data-avatar-size="48"></span>
              <span class="partner-headcard__avatar-item" data-profile-avatar="partner" data-avatar-size="48"></span>
            </span>
            <div class="account-profile__info partner-headcard__info">
              <div class="partner-headcard__identity">
                <div class="account-profile__name-row">
                  <p class="account-profile__name">${coupleTitle}</p>
                  ${this._renderPlanChip()}
                </div>
                ${metaLine}
              </div>
              ${disconnectBtn}
            </div>
          </div>
          ${errorBlock}
          <div class="partner-headcard__sharing">${this._renderSharingRegion()}</div>
          ${this.confirmingDisconnect
    ? `<div class="partner-headcard__confirm">${this._renderDisconnectConfirm(name)}</div>` : ''}
        </div>
      </section>
    `;
  }

  /* ----- The sharing region inside the Account card: the single Partner
   * Sharing master switch and the W9 first-feed notice. Per-unit consent
   * (and, while locked, the foundations gate strip) lives on the Sharing
   * pill. ----- */

  _renderSharingRegion() {
    const name = escapeHtml(this.partnerDisplayName());

    if (this.sharingLoadStatus === 'unavailable') {
      return `
        <p class="account-section__note">Sharing is coming soon — you’ll be able to choose what you share with ${name} here.</p>
        ${renderSwitchRow({
    label: SHARING_COPY.master.title,
    hint: SHARING_COPY.master.hint(name),
    id: 'partner-understanding-label', on: false, disabled: true, action: 'sharing-understanding'
  })}`;
    }
    // The fetch itself failed → say so and offer a retry (never a stuck "Loading…").
    if (this.sharingLoadStatus === 'error') {
      return this._sharingNotice(
        'Something went wrong',
        escapeHtml(this.sharingError || 'Could not load your sharing settings.'),
        { retry: true }
      );
    }
    if (this.sharingLoadStatus !== 'loaded' || !this.sharing) {
      return '<p class="account-section__note" aria-busy="true">Loading…</p>';
    }

    const v = this.sharing;

    // ----- W9 first-feed notice. Independent of gestalt state, and this region
    // is its only dismiss surface — it rides directly under the switch. -----
    const noticeRow = v.firstFeedNoticePending
      ? `<div class="account-row partner-memo-feed-notice">
           <div class="account-row__body">
             <span class="account-row__label">Your notes now help ${name}’s guidance</span>
             <span class="account-row__value account-row__value--wrap">Notes you’ve sent ${name} can gently shape how their guidance understands you. You can turn this off anytime.</span>
           </div>
           <div class="account-row__action">
             <button type="button" class="account-btn" data-action="sharing-memo-feed-notice-dismiss">Got it</button>
           </div>
         </div>`
      : '';

    // ----- The Partner Sharing master — the discovery MASTER and sole pause
    // gate (there is no separate revoke), now also gating the notes feed. It is
    // NEVER disabled by gestalt state (locked/generating/error): the notes half
    // has no Foundations gate, so a pre-gate user must still be able to pause
    // it. Only an in-flight PATCH disables it. It reflects `masters.discovery`
    // and nothing else: it gates whether decided units may cross, not whether
    // any have been decided yet. The subtitle always explains what the switch
    // governs; a state note rides below it rather than replacing the
    // explanation. See SHARING_STATE_NOTES for which states get one, and why
    // only `locked` gets the amber treatment. -----
    const masterOn = v.masters?.discovery !== false;
    const note = SHARING_STATE_NOTES[v.state];
    const noteTone = note?.tone ? ` partner-row__state--${note.tone}` : '';
    const masterRow = renderSwitchRow({
      label: SHARING_COPY.master.title,
      hint: SHARING_COPY.master.hint(name),
      id: 'partner-understanding-label',
      on: masterOn,
      disabled: this._sharingBusy || this._sharingSubmitting,
      action: 'sharing-understanding'
    });
    // Sibling of the row, not a child of its body — the body shares a flex line
    // with the toggle, so a tinted note nested there stops short of the card edge.
    const stateNote = note
      ? `<p class="partner-row__state${noteTone}">${note.text}</p>`
      : '';

    // The locked-state foundations gate (who still has journey left to
    // finish) used to render here too, but that duplicated the Sharing pill's
    // own locked notice with no way to act on it from Overview. It now lives
    // solely on the Sharing pill (`_renderSharingTab`), which is where a user
    // actually lands wanting to do something about it.
    return `${masterRow}${noticeRow}${stateNote}`;
  }

  /**
   * The two-person foundations gate, shown on the Sharing pill under its
   * locked-state note: who has finished the journey and who hasn't. Keeps the
   * reason assessment sharing is unavailable legible rather than leaving a
   * bare hint — Overview only carries the one-line note; this detail (and the
   * CTA to clear it) lives where the user actually lands wanting to act.
   *
   * This block renders ONLY in the `locked` state, and the gate is per-partner —
   * so the VIEWER is always the outstanding one (a done viewer would be in
   * generating/ready/waiting, never locked). The partner row is informational
   * context, not a blocker. The layout still lets either row carry a bar: the
   * unfinished row gets the weight (a bar, and — when it's the user — the action
   * that clears the gate), while a finished row is quiet reassurance.
   *
   *   ┌──────────────────────────────────────────────┐
   *   │ (av)  You                       In progress  │
   *   │       ▓▓▓▓▓▓▓░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░  │
   *   │ (av)  Laurel        ✓ Completed June 25, 2026│
   *   │            [ Continue your journey → ]       │
   *   └──────────────────────────────────────────────┘
   *
   * The status word sits on the name's line rather than beside the bar, and the bar
   * carries no percent label: two numbers-ish readings competing across one row made
   * neither land. The bar then spans the full body width and reads as one gesture.
   * The status says only "Complete" / "In progress" — the word "foundations" is
   * already in the state note above.
   *
   * Sized as the page's headline content, not a footnote: 44px avatars (there is
   * no competing identity avatar on this tab to defer to, unlike when this block
   * sat under Overview's switch) and the bar at full `.understanding__bar-*`
   * weight (10px), matching the path bars on /discovery exactly rather than the
   * 6px trim that read right as a subordinate note. No boxed-off inset background
   * either — the rows sit directly on the card, since there's no switch above
   * them on this tab for an inset to visually subordinate to.
   *
   * The bar is a single composite across both gate paths (see `computeGateDisplay`
   * in routes/discoveryCouples.js). `percent` is null when the backend couldn't
   * read path progress — the row then degrades to a plain status word rather than
   * showing a misleading empty bar.
   */
  _renderGateProgress(v) {
    const gate = v.gate || {};
    const partnerName = escapeHtml(v.partnerName || this.partnerDisplayName());
    const you = gate.you || {};
    const partner = gate.partner || {};

    const person = (label, p, avatarRole) => {
      const hasBar = !p.done && typeof p.percent === 'number';
      const completedOn = p.done ? formatLongDate(p.completedAt) : '';
      // "Not started" rather than "In progress" at zero — an empty bar labelled
      // "In progress" reads as broken.
      // One line, not a stacked word + date: this row is the reassurance, and the
      // stack made it the tallest thing in a block whose point is the OTHER row.
      // Long date format matches "Linked June 25, 2026" in the Account card above.
      const status = p.done
        ? (completedOn ? `Completed ${completedOn}` : 'Complete')
        : (hasBar && p.percent === 0 ? 'Not started' : 'In progress');

      return `
        <li class="partner-sharing-progress__item">
          <span class="partner-sharing-progress__avatar" data-profile-avatar="${avatarRole}"
                data-avatar-size="44" aria-hidden="true"></span>
          <div class="partner-sharing-progress__body">
            <div class="partner-sharing-progress__head">
              <span class="partner-sharing-progress__name">${label}</span>
              <div class="partner-sharing-progress__status${p.done ? ' partner-sharing-progress__status--done' : ''}">
                ${p.done
    ? `<span class="partner-sharing-progress__mark partner-sharing-progress__mark--done"
                       aria-hidden="true">${ICONS.check}</span>` : ''}
                <span class="partner-sharing-progress__status-text">${escapeHtml(status)}</span>
              </div>
            </div>
            ${hasBar ? `
              <span class="partner-sharing-progress__bar">
                <span class="understanding__bar-track" aria-hidden="true">
                  <span class="understanding__bar-fill" style="width:${p.percent}%"></span>
                </span>
              </span>` : ''}
          </div>
        </li>`;
    };

    // Only the user's own unfinished journey gets an action — when the outstanding
    // side is the partner there is nothing this user can do, so the rows stay
    // status-only rather than dangling a button that nags someone else.
    const cta = (!you.done && you.nextPath?.id)
      ? `<button type="button" class="account-btn account-btn--primary partner-sharing-progress__cta"
                 data-action="gate-continue" data-path-id="${escapeHtml(you.nextPath.id)}"
                 data-path-name="${escapeHtml(you.nextPath.name)}">
           Continue ${escapeHtml(you.nextPath.name)}
         </button>`
      : '';

    return `
      <div class="partner-sharing-progress-block">
        <ul class="partner-sharing-progress">
          ${person('You', you, 'self')}
          ${person(partnerName, partner, 'partner')}
        </ul>
        ${cta}
      </div>
    `;
  }

  /**
   * URL params for /partner. `tab` selects the linked-state pill (default
   * 'overview'), same enum-with-default pattern as DiscoverySection.urlParamSchema.
   * `share` is a one-shot entry intent (the dashboard sharing nudge routes
   * `/partner?tab=sharing&share=1`): land on the consent registry and scroll it
   * into view, then scrub the param. `queue` is a sibling one-shot intent
   * (the dashboard nudge's deep link, design §3.2: `/partner?tab=sharing&queue=<groupId>`):
   * auto-open the ceremony queue for that group, then scrub. Same static-schema
   * pattern as PartnerNotesSection.urlParamSchema (its `info` param is a string;
   * `share` is a genuine boolean flag; `queue` is a string carrying a group id).
   */
  static urlParamSchema = {
    tab: { type: 'enum', enum: ['overview', 'sharing'], default: 'overview' },
    share: { type: 'boolean' },
    queue: { type: 'string' }
  };

  /**
   * Apply routed URL params. The sharing view usually isn't loaded yet when the
   * router calls this (activate() fires `_loadSharingStatus` without awaiting),
   * so the `share`/`queue` intents are each consumed in two places: here when
   * already loaded (warm / in-place navigation), else by `_loadSharingStatus`'s
   * post-load render.
   * @param {Object} [values] - Parsed values from URLParamsHandler
   */
  applyURLParams(values) {
    // `share`/`queue` each name the consent registry, which is the Sharing
    // pill — so either selects that pill on its own, without depending on the
    // caller to pair it with `tab=sharing` (older links, bookmarks).
    const tab = (values?.tab === 'sharing' || values?.share || values?.queue) ? 'sharing' : 'overview';
    if (this.state === 'linked' && tab !== this.currentTab) {
      this.currentTab = tab;
      this.render();
    }
    if (values?.queue) {
      this._queueIntent = values.queue;
      if (this.sharingLoadStatus === 'loaded') this._consumeQueueIntent();
    }
    if (!values?.share) return;
    this._scrollToSharing = true;
    if (this.sharingLoadStatus === 'loaded') this._consumeScrollIntent();
  }

  /**
   * Scroll the sharing surface into view — the shared fallback behind both
   * one-shot intents (`share`, and `queue` when it can't open the queue).
   * Prefers the registry (the Sharing pill's ledger), falling back to
   * Overview's Account card (which hosts the sharing master) when the
   * registry isn't the surface on screen.
   */
  _scrollSharingIntoView() {
    const card = this.container.querySelector('.partner-sharing-registry')
      || this.container.querySelector('.partner-headcard');
    if (card) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * One-shot: scroll the sharing surface into view and scrub `?share=1` so a
   * refresh or bookmark doesn't re-trigger it (the /notes ?info= precedent).
   */
  _consumeScrollIntent() {
    this._scrollToSharing = false;
    this._scrollSharingIntoView();
    const url = new URL(window.location.href);
    if (url.searchParams.has('share')) {
      url.searchParams.delete('share');
      page.replace(url.pathname + (url.search ? url.search : ''));
    }
  }

  /**
   * One-shot: auto-open the ceremony queue for `?queue=<groupId>` (the
   * dashboard nudge's deep link, design §3.2), then scrub the param so a
   * refresh doesn't re-fire it.
   *
   * Never a silent dead click (review R5): degrades to the scroll fallback
   * when the registry isn't mounted — Overview surface, unlinked, load
   * failure, or a not-actionable state (`locked`/`generating`/`error` all
   * leave `registryLedger` null, so `_registryActive()` already reads false
   * for them — see `_syncRegistryLedger`) — or when the group's scope
   * resolves empty. The registry's own banners explain the state either way.
   */
  _consumeQueueIntent() {
    const groupId = this._queueIntent;
    this._queueIntent = null;
    const registry = this._sharingRegistry;
    const openable = this._registryActive() && !!registry
      && queueScope(registry.units, groupId).length > 0;
    if (openable) {
      registry.openQueue(groupId);
    } else {
      this._scrollSharingIntoView();
    }
    const url = new URL(window.location.href);
    if (url.searchParams.has('queue')) {
      url.searchParams.delete('queue');
      page.replace(url.pathname + (url.search ? url.search : ''));
    }
  }

  /**
   * Switch the linked-state pill. Unlike DiscoverySection's push-then-
   * route-reentry dance, `render()` here always does a full innerHTML replace
   * and every listener is delegated on the container (attachListeners()), so
   * updating state and rerendering directly is correct and simpler — no
   * incremental DOM patching to keep in sync.
   * @param {'overview'|'sharing'} tab
   */
  _switchTab(tab) {
    if (tab === this.currentTab) return;
    this.currentTab = tab;
    const url = new URL(window.location.href);
    if (tab === 'overview') url.searchParams.delete('tab');
    else url.searchParams.set('tab', tab);
    page.replace(url.pathname + (url.search ? url.search : ''));
    this.render();
  }

  async activate() {
    if (this.isActive) return;
    this.isActive = true;
    this._resetTransientState();
    this.state = 'loading';
    this.partner = null;
    this.invitation = null;
    this.errorMessage = '';
    this.render();
    this.attachListeners();

    // DEV-ONLY: `?mockState=` on localhost feeds a hand-written view-model so
    // every Sharing state/card variant is previewable without backend data.
    if (this._mockActive()) {
      await this._applyMockState();
      return;
    }

    // Billing context for the linked state (chip / unlink consequence / upsell).
    // Deliberately not awaited: the link status is what the page renders on,
    // and the billing surfaces fill in a moment later.
    this._loadBillingContext();

    await this._loadInitialState();
  }

  /**
   * Resolve the billing context the linked state needs: the shared
   * subscription snapshot (is the partner rate applied to the viewer's own
   * Premium?) and the price config (is the partner rate offered at all?). Both
   * are best-effort — every consumer degrades to "no partner rate in play"
   * when they don't resolve.
   */
  async _loadBillingContext() {
    const [status, config] = await Promise.all([
      fetchSubscriptionStatus(),
      SubscriptionAPI.getConfig()
    ]);
    if (!this.isActive) return;
    this.subscriptionStatus = status;
    this.partnerRateConfig = (config?.success && config.data?.partnerRate) || null;
    // Only the linked state renders any of this, and re-rendering mid-confirm
    // would rebuild the typed-token input under the user's cursor.
    if (this.state === 'linked' && !this.confirmingDisconnect) this.render();
  }

  /* -------------------------------------------------------------------- *
   * DEV-ONLY mock-preview harness (localhost + ?mockState=). Never reached *
   * in production: gated on hostname and the fixtures load via dynamic     *
   * import() into a lazy chunk that prod never fetches. See                *
   * partnerSharingFixtures.js.                                             *
   * -------------------------------------------------------------------- */

  _mockActive() {
    return typeof window !== 'undefined'
      && window.location.hostname === 'localhost'
      && new URLSearchParams(window.location.search).has('mockState');
  }

  _mockStateKey() {
    return new URLSearchParams(window.location.search).get('mockState');
  }

  async _applyMockState() {
    let mod;
    try {
      mod = await import('./partnerSharingFixtures.js');
    } catch (_err) {
      // Fixtures missing (e.g. deleted) → fall back to the real load.
      await this._loadInitialState();
      return;
    }
    if (!this.isActive) return;
    // Stash the fixture list for the switcher, which renders synchronously and so
    // can't await this lazy chunk itself — keeps one list of states, not two.
    this._mockOrder = mod.MOCK_ORDER;
    const fx = mod.MOCK_SHARING[this._mockStateKey()] || mod.MOCK_SHARING.ready;
    this.partner = ('partner' in fx) ? fx.partner : mod.buildMockPartner();
    this.state = fx.topState || 'linked';
    this.invitation = fx.invitation || null;
    this.sharing = fx.view || null;
    this.sharingLoadStatus = fx.loadStatus || (fx.view ? 'loaded' : 'idle');
    // Deep clone: MOCK_SHARING is a module singleton shared for the page's
    // lifetime, and the registry writes approvals straight into this object.
    this.registryLedger = fx.registry ? structuredClone(fx.registry) : null;
    this.render();
  }

  /** Floating state-switcher rendered only while the mock harness is active. */
  _renderMockSwitcher() {
    if (!this._mockActive()) return '';
    const current = this._mockStateKey();
    const base = window.location.pathname;
    // Preserve `tab=` across a state switch: since the locked-state gate strip
    // moved onto the Sharing pill, losing the tab on every link would silently
    // drop a Sharing-tab preview back to Overview's terse note.
    const tab = new URLSearchParams(window.location.search).get('tab');
    const tabQuery = tab ? `&tab=${tab}` : '';
    const links = (this._mockOrder || [])
      .map(k => `<a class="partner-mock__link ${k === current ? 'partner-mock__link--on' : ''}"
                    rel="external" href="${base}?mockState=${k}${tabQuery}">${k}</a>`)
      .join('');
    return `
      <div class="partner-mock" role="note" aria-label="Mock state switcher (dev only)">
        <span class="partner-mock__tag">MOCK</span>
        ${links}
        <a class="partner-mock__link partner-mock__link--exit" rel="external" href="${base}">✕ exit</a>
      </div>
    `;
  }

  deactivate() {
    if (!this.isActive) return;
    this.isActive = false;
    this._clearCopyTimer();
    this._clearSharingPoll();
    this._destroySharingRegistry();
    this.detachListeners();
    this.stickyStack?.destroy();
    this.stickyStack = null;
  }

  /**
   * Reset all inline UI flags. Called on activate and after destructive
   * transitions (link/unlink) to prevent stale flags leaking across states.
   */
  _resetTransientState() {
    // applyURLParams corrects this from the URL right after activate() —
    // reset to the default here so a repeat visit can't flash the pill left
    // active from a previous session before that correction lands.
    this.currentTab = 'overview';
    this.confirmingDisconnect = false;
    this.disconnecting = false;
    this.disconnectConfirmText = '';
    this.inviteChannel = 'link';
    this.sendingEmail = false;
    this.emailError = '';
    this.copied = false;
    this._clearCopyTimer();
    this.confirmingCancelInvite = false;
    this.cancellingInvite = false;
    this.recreating = false;
    this.sharingLoadStatus = 'idle';
    this.sharing = null;
    this.sharingError = '';
    // Rebuilt from the sharing-status read (or re-cloned from the fixture by
    // _applyMockState()) on this same activate(); clearing here is what makes a
    // fresh visit start from server truth again.
    this.registryLedger = null;
    this._scrollToSharing = false;
    this._queueIntent = null;
    this._sharingBusy = false;
    this._sharingSubmitting = false;
    this._sharingPollAttempts = 0;
    this._sharingPollState = null;
    this._clearSharingPoll();
  }

  _clearCopyTimer() {
    if (this._copyRevertTimer) {
      clearTimeout(this._copyRevertTimer);
      this._copyRevertTimer = null;
    }
  }

  attachListeners() {
    this.container.addEventListener('click', this.handleClick);
    this.container.addEventListener('keydown', this.handleKeydown);
    this.container.addEventListener('input', this.handleInput);
    this.container.addEventListener('submit', this.handleSubmit);
  }

  detachListeners() {
    this.container.removeEventListener('click', this.handleClick);
    this.container.removeEventListener('keydown', this.handleKeydown);
    this.container.removeEventListener('input', this.handleInput);
    this.container.removeEventListener('submit', this.handleSubmit);
  }

  /* -------------------------------------------------------------------- *
   * Data loading                                                         *
   * -------------------------------------------------------------------- */

  /**
   * Load the user's link status. If linked, we're done — render linked.
   * If unlinked, fetch (or create) the active invitation and render active.
   */
  async _loadInitialState() {
    try {
      const partnerResult = await this.discoveryApi.getPartner();
      if (!this.isActive) return;
      const partner = partnerResult?.data || null;
      if (partner) {
        this.partner = partner;
        this.state = 'linked';
        this.render();
        // Load the Sharing view model in the background — the Settings status
        // block needs it too, so it's fetched regardless of the active tab.
        this._loadSharingStatus();
        return;
      }
      await this._loadInvitation();
    } catch (err) {
      if (!this.isActive) return;
      console.error('[PartnerSection] Failed to load partner:', err?.error || err);
      this.state = 'error';
      this.errorMessage = err?.error || 'Could not load partner information.';
      this.render();
    }
  }

  /**
   * Idempotent create-or-fetch for the active invitation. Used on initial
   * load when unlinked, and again after the user cancels and clicks
   * "Create a new invitation".
   */
  async _loadInvitation() {
    try {
      const result = await this.discoveryApi.createOrGetInvitation();
      if (!this.isActive) return;
      this.invitation = result?.data || null;
      this.state = 'active';
      this.recreating = false;
      this.render();
    } catch (err) {
      if (!this.isActive) return;
      console.error('[PartnerSection] Failed to load invitation:', err?.error || err);
      this.state = 'error';
      this.errorMessage = err?.error || 'Could not create your invitation link.';
      this.recreating = false;
      this.render();
    }
  }

  /* -------------------------------------------------------------------- *
   * Display helpers                                                      *
   * -------------------------------------------------------------------- */

  partnerDisplayName() {
    return this.partner?.firstName
      || this.partner?.name
      || this.partner?.username
      || 'your partner';
  }

  /** The signed-in user's half of the "Doug & Laurel" couple title. */
  _selfDisplayName() {
    return this.currentUser?.firstName
      || this.currentUser?.name
      || this.currentUser?.username
      || 'You';
  }

  /**
   * The signed-in user, for the "You" avatar in the foundations gate strip. Read
   * from the app rather than held as state — the page never mutates it, and the
   * avatar helper degrades to a generic icon if it isn't there yet.
   *
   * @returns {Object|null}
   */
  get currentUser() {
    return window.app?.currentUser || null;
  }

  rerender({ focus } = {}) {
    this.render();
    if (focus === 'disconnect-input') {
      const input = this.container.querySelector('#partner-disconnect-input');
      if (input) input.focus();
    } else if (focus === 'email-input') {
      const input = this.container.querySelector('#partner-email-input');
      if (input instanceof HTMLInputElement) {
        input.focus();
        // Caret to the end so a resend edits rather than overwrites the address.
        // NOT setSelectionRange: the spec only defines it for text/search/url/
        // tel/password, and it throws InvalidStateError on type="email" — which
        // it was doing on every focus here. Re-assigning the value parks the
        // caret at the end on every browser and never throws.
        const { value } = input;
        input.value = '';
        input.value = value;
      }
    }
  }

  /**
   * Destroy-before-swap, mount-after-attach (the `PracticesSection.render()` →
   * `_mountFilterBar()` precedent): the registry owns a `ResultsFilterBar`
   * whose document-level listeners are released only by `destroy()`, and it
   * mounts into markup that doesn't exist until the innerHTML lands.
   */
  render() {
    this._destroySharingRegistry();
    this.container.innerHTML = this._renderPage();
    this._populateProfileAvatars();
    this._initStickyStack();
    this._mountSharingRegistry();
    Tooltip.setupAll(this.container, '[data-tooltip]', { position: 'below' });
  }

  /**
   * Populate every avatar slot created by the current full-page render.
   * @private
   * @returns {void}
   */
  _populateProfileAvatars() {
    this.container.querySelectorAll('[data-profile-avatar]').forEach(slot => {
      const user = slot.dataset.profileAvatar === 'self' ? this.currentUser : this.partner;
      const size = Number.parseInt(slot.dataset.avatarSize, 10);
      slot.replaceChildren(createProfileAvatar(user, { size }));
    });
  }

  /**
   * Whether this render should hand the Sharing pill to the consent registry.
   *
   * Mock-first: under the `?mockState=` harness the ledger is the fixture's,
   * and `_loadSharingStatus` is inert, so a fixture can never be overwritten by
   * a real read. Off the harness the ledger comes from the sharing-status
   * payload, and is null until there are share units to decide.
   *
   * @returns {boolean}
   */
  _registryActive() {
    return this.currentTab === 'sharing' && !!this.registryLedger;
  }

  /**
   * Mount the registry into the freshly-rendered shell. No-op unless the gate
   * is satisfied and the shell is present, and idempotent — `activate()`
   * renders twice under the mock harness (once before the lazy fixture import
   * resolves, once from `_applyMockState()`).
   *
   * `mockMode` short-circuits the registry's three consent seams to local
   * mutation; off the harness they run through `api`, whose two callbacks are
   * this section's (it owns the API client and the view model both seams
   * update).
   */
  _mountSharingRegistry() {
    if (this._sharingRegistry || !this._registryActive()) return;
    const root = this.container.querySelector('[data-sharing-registry]');
    if (!root) return;
    this._sharingRegistry = new SharingRegistry({
      root,
      ledger: this.registryLedger,
      eventBus: this.eventBus || eventBus,
      partnerName: this.partnerDisplayName(),
      mockMode: this._mockActive(),
      api: {
        setUnitState: (unitId, state) => this._commitSharingUnit(unitId, state),
        submitSharing: (unitStates) => this._commitSharingSubmit(unitStates)
      }
    });
  }

  /** Release the registry (filter bar, listeners, its own right panel). */
  _destroySharingRegistry() {
    this._sharingRegistry?.destroy();
    this._sharingRegistry = null;
  }

  /**
   * (Re)bind the sticky header stack to the freshly-rendered DOM: title →
   * pill bar (linked state only). Idempotent — safe to call on every render.
   */
  _initStickyStack() {
    this.stickyStack?.destroy();
    this.stickyStack = null;
    const root = this.container.querySelector('.explore-view--list');
    if (root) this.stickyStack = new StickyStack(root);
  }

  /* ----- Small shared building blocks ----- */

  _sharingNotice(title, body, { retry = false } = {}) {
    return `
      <section class="partner-sharing__notice">
        <h3 class="partner-sharing__notice-title">${escapeHtml(title)}</h3>
        <p class="partner-sharing__notice-body">${body}</p>
        ${retry ? `
          <div class="partner-sharing__notice-action">
            <button type="button" class="account-btn" data-action="sharing-retry">Try again</button>
          </div>` : ''}
      </section>
    `;
  }

  /**
   * Back-arrow link rendered above the settings content when the user is
   * mid-orientation. The user clicked "link partner" from orientation to
   * get here, so context is already established — a single back-link
   * gives them the return path without restating what they already know.
   */
  _renderOrientationBackLink() {
    if (!this._isInOrientation()) return '';
    if (this.state === 'linked' || this.state === 'loading' || this.state === 'error') {
      return '';
    }
    return `
      <button type="button"
              class="partner-page__back-link"
              data-action="orientation-return">
        <span class="partner-page__back-link-arrow" aria-hidden="true">\u2190</span>
        Return to orientation
      </button>
    `;
  }

  /* -------------------------------------------------------------------- *
   * Loading / error                                                      *
   * -------------------------------------------------------------------- */

  _renderLoadingSection() {
    return `
      <section class="account-section">
        <div class="card account-section__card" aria-busy="true">
          <p class="account-row__value">Loading\u2026</p>
        </div>
      </section>
    `;
  }

  _renderErrorSection() {
    return `
      <section class="account-section">
        <div class="card account-section__card">
          <div class="account-row">
            <div class="account-row__body">
              <span class="account-row__label">Something went wrong</span>
              <span class="account-row__value">${escapeHtml(this.errorMessage)}</span>
            </div>
            <div class="account-row__action">
              <button type="button" class="account-btn" data-action="retry">Try again</button>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  _renderDisconnectConfirm(name) {
    const disabled = this.disconnecting ? 'disabled' : '';
    const ready = this.disconnectConfirmText === DISCONNECT_TOKEN;
    const confirmDisabled = (!ready || this.disconnecting) ? 'disabled' : '';
    const errorBlock = this.errorMessage
      ? `<p class="account-form__error" role="alert">${escapeHtml(this.errorMessage)}</p>`
      : '';
    const billingConsequence = this._disconnectBillingConsequence();

    return `
      <div class="account-row account-row--stacked" role="alertdialog"
           aria-labelledby="partner-disconnect-title"
           aria-describedby="partner-disconnect-desc">
        <div class="account-row__body">
          <span class="account-row__label" id="partner-disconnect-title">
            Disconnect from ${name}?
          </span>
        </div>
      </div>

      <div class="partner-disconnect-warning" id="partner-disconnect-desc">
        <p class="partner-disconnect-warning__lead">
          Disconnecting ends your shared link with ${name}. You\u2019ll both
          keep your individual data, but:
        </p>
        <ul class="partner-disconnect-warning__list">
          <li>shared progress and joint insights stop updating;</li>
          <li>if you reconnect later, you\u2019ll start a fresh shared
            context — past joint history won\u2019t carry over.</li>
        </ul>
        ${billingConsequence
    ? `<p class="partner-disconnect-warning__billing">${escapeHtml(billingConsequence)}</p>`
    : ''}
      </div>

      <div class="account-form">
        <div class="account-form__field">
          <label class="account-form__label" for="partner-disconnect-input">
            Type <strong>${DISCONNECT_TOKEN}</strong> below to confirm.
          </label>
          <input type="text"
                 id="partner-disconnect-input"
                 class="account-form__input"
                 value="${escapeHtml(this.disconnectConfirmText)}"
                 autocomplete="off"
                 autocapitalize="characters"
                 spellcheck="false"
                 ${disabled}
                 aria-describedby="partner-disconnect-desc" />
        </div>
        ${errorBlock}
        <div class="account-form__actions">
          <button type="button" class="account-btn account-btn--ghost"
                  data-action="cancel-disconnect" ${disabled}>Cancel</button>
          <button type="button" id="partner-disconnect-confirm"
                  class="account-btn account-btn--danger"
                  data-action="confirm-disconnect" ${confirmDisabled}>
            ${this.disconnecting ? 'Disconnecting\u2026' : 'Disconnect partner'}
          </button>
        </div>
      </div>
    `;
  }

  /* -------------------------------------------------------------------- *
   * Active (unlinked + active invitation) state                          *
   * One card, two channels, a segmented switch between them; FAQ below.  *
   * -------------------------------------------------------------------- */

  _renderInviteSections() {
    // Wrap in a single .partner-invite block so the parent
    // .settings-column flex-gap doesn't push the card / cancel link /
    // FAQ apart by var(--space-6) — the invite area owns its own rhythm.
    //
    // CARDLESS. The page header now carries the title and the explainer, so a
    // card here would be a box drawn around a single field with no heading of
    // its own. That also settles the field's chrome: on the page background a
    // raised white pill has something to cast against, which is the /search
    // .insights-search recipe — an inset grey field only works inside a card.
    // The two travel together; don't reintroduce one without the other.
    //
    // The channel swap is the pre-fe06e88e behaviour, restored with its two
    // actual defects fixed: the toggle is a PERSISTENT landmark (you can always
    // see both channels exist and which one you're in — the old version simply
    // vaporised the link and offered a bare "Cancel"), and both channels render
    // the same two rows (field / footer) so switching can't jump the layout.
    return `
      <div class="partner-invite">
        ${this._renderChannelToggle()}
        ${this.inviteChannel === 'email'
          ? this._renderEmailChannel()
          : this._renderLinkChannel()}
        ${this._renderCancelFooter()}
        ${this._renderFaq()}
      </div>
    `;
  }

  /**
   * The channel switch — the one control that stays put across the swap.
   *
   * Uses the shared .page-toggle (components/page-toggle.css), the same
   * page-level pill switch as /notes' Inbox ↔ Sent. It was extracted from
   * partner-notes.css for this second caller.
   *
   * ARIA deliberately diverges from /notes' usage: aria-pressed buttons in a
   * labelled group rather than role="tablist"/"tab". A tablist promises
   * arrow-key navigation and a roving tabindex, neither of which exists here —
   * an incomplete tab implementation is worse for a screen-reader user than an
   * honest pair of toggle buttons.
   */
  _renderChannelToggle() {
    const btn = (channel, icon, label) => {
      const on = this.inviteChannel === channel;
      return `
        <button type="button"
                class="page-toggle__btn ${on ? 'page-toggle__btn--active' : ''}"
                aria-pressed="${on ? 'true' : 'false'}"
                data-action="invite-channel" data-channel="${channel}"
                ${this.sendingEmail ? 'disabled' : ''}>
          <span class="page-toggle__icon" aria-hidden="true">${icon}</span>
          <span>${label}</span>
        </button>`;
    };
    return `
      <div class="page-toggle-row partner-invite__toggle-row">
        <div class="page-toggle" role="group" aria-label="Invitation method">
          ${btn('link', ICONS.link, 'Link')}
          ${btn('email', ICONS.mail, 'Email')}
        </div>
      </div>
    `;
  }

  /**
   * Link channel: the invitation URL, Copy, and (where the platform actually
   * has a share sheet) a labelled Share button.
   *
   * CLICKING ANYWHERE IN THE FIELD COPIES, which is the expected behaviour for
   * a readonly, single-purpose value — there is no reason to place a caret in
   * a URL you can't edit. It is layered on as a MOUSE CONVENIENCE, not as a
   * control: no role="button" on the wrapper, and the input keeps its label,
   * its place in the tab order, and its selectability.
   *
   * That distinction is the whole lesson of the version this replaces (removed
   * in fe06e88e), which made the box role="button" AND the input aria-hidden +
   * tabindex="-1" + pointer-events:none. That hid the URL from screen readers
   * and killed the clipboard-failure fallback in _handleCopy, which works by
   * focusing and selecting this very input. The Copy button stays regardless:
   * it is the discoverable affordance, and its label is what tells a first-time
   * user that clicking does anything at all.
   *
   * Share is omitted entirely where navigator.share is absent — most desktop
   * browsers have no native share UI at all, so a permanent slot for it would
   * be dead chrome. Where it does exist it stays SECONDARY rather than a peer
   * of Copy: Copy has to mean the same thing on every device. It always
   * carries a text label; the share glyph has no cross-platform reading on
   * its own.
   */
  _renderLinkChannel() {
    const url = this.invitation?.url || '';
    const isCopied = this.copied;

    return `
      <div class="partner-invite__channel">
        <div class="partner-invite__box partner-invite__box--copy" data-action="invite-copy">
          <span class="partner-invite__box-icon" aria-hidden="true">${ICONS.link}</span>
          <input type="text"
                 class="partner-invite__input partner-invite__input--mono"
                 id="partner-invite-link"
                 readonly
                 value="${escapeHtml(url)}"
                 aria-label="Invitation link" />
          <button type="button"
                  class="partner-invite__primary ${isCopied ? 'partner-invite__primary--copied' : ''}"
                  data-action="invite-copy">
            <span class="partner-invite__primary-icon" aria-hidden="true">${isCopied ? ICONS.check : ICONS.copy}</span>
            <!-- role="status" so the copy is announced when the click landed on
                 the field rather than the button — the label change is the only
                 confirmation, and it happens away from the user's focus. -->
            <span role="status">${isCopied ? 'Copied' : 'Copy'}</span>
          </button>
        </div>
        ${this._renderChannelFooter('Paste it into a text, chat, or DM.', { share: true })}
      </div>
    `;
  }

  /**
   * The footer block both channels render: a centred helper line, with the
   * Share button INLINE beside it on the link channel where the platform has a
   * share sheet.
   *
   * Inline is what keeps the two channels the same height for free — the button
   * is shorter than the row it already sits in, so a channel without one is no
   * shorter. Stacking Share BELOW the helper instead forced both channels to
   * reserve a 67px block, which pushed "Cancel invitation" and the whole FAQ
   * down by a button-row on the email channel, where no button justifies it.
   *
   * @param {string} helperHtml - Helper line content (may contain <strong>)
   * @param {Object} [opts]
   * @param {boolean} [opts.share] - Whether this channel offers Share
   * @returns {string} HTML string
   */
  _renderChannelFooter(helperHtml, { share = false } = {}) {
    const shareAvailable = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
    return `
      <div class="partner-invite__footer">
        <p class="partner-invite__helper">${helperHtml}</p>
        ${share && shareAvailable
          ? `<button type="button" class="partner-invite__share-btn" data-action="invite-share">
               <span class="partner-invite__share-icon" aria-hidden="true">${ICONS.share}</span>
               <span>Share…</span>
             </button>`
          : ''}
      </div>
    `;
  }

  /**
   * Email channel: same geometry as the link channel, so the swap reads as one
   * component changing rather than two different things trading places.
   *
   * The email is sent server-side (POST …/:id/send-email) — a real branded
   * invitation, not a mailto: handoff into whatever client the OS happens to
   * have configured — which is why it earns a peer channel rather than the
   * 13px text link it used to be.
   *
   * No Cancel action: the segmented control above is the way back, and it is
   * always visible. A Cancel here would imply there's something to abandon.
   *
   * The helper slot carries "Last sent to…" when there is one — that's
   * email-channel state, and it used to render under the LINK field, orphaned
   * from the action that produced it. Falling back to the static line keeps
   * the row occupied either way so the card height never moves.
   */
  _renderEmailChannel() {
    const sentEmail = this.invitation?.inviteeEmail || '';
    const sentDate = formatLongDate(this.invitation?.inviteeEmailSentAt);
    const errorBlock = this.emailError
      ? `<p class="account-form__error" role="alert">${escapeHtml(this.emailError)}</p>`
      : '';
    const sending = this.sendingEmail;
    const helper = sentEmail
      ? `Last sent to <strong>${escapeHtml(sentEmail)}</strong>${sentDate ? ` on ${escapeHtml(sentDate)}` : ''}.`
      : 'We’ll send them a short invitation from encouple.';

    return `
      <form class="partner-invite__channel"
            data-form="invite-email" autocomplete="off" novalidate>
        <div class="partner-invite__box">
          <span class="partner-invite__box-icon" aria-hidden="true">${ICONS.mail}</span>
          <input type="email"
                 id="partner-email-input"
                 class="partner-invite__input"
                 name="email"
                 value="${escapeHtml(sentEmail)}"
                 placeholder="name@example.com"
                 aria-label="Partner\u2019s email"
                 autocomplete="email"
                 spellcheck="false"
                 ${sending ? 'disabled' : ''} />
          <button type="submit"
                  class="partner-invite__primary"
                  data-action="invite-email-submit"
                  ${sending ? 'disabled aria-busy="true"' : ''}>
            <span class="partner-invite__primary-icon" aria-hidden="true">${ICONS.send}</span>
            <span>${sending ? 'Sending\u2026' : (sentEmail ? 'Resend' : 'Send')}</span>
          </button>
        </div>
        ${errorBlock}
        ${this._renderChannelFooter(helper)}
      </form>
    `;
  }

  _renderCancelFooter() {
    if (this.confirmingCancelInvite) {
      const disabled = this.cancellingInvite ? 'disabled' : '';
      return `
        <div class="partner-invite__cancel-confirm" role="alertdialog"
             aria-label="Cancel this invitation?">
          <p class="partner-invite__cancel-confirm-text">
            Cancel this invitation? The link will stop working immediately.
          </p>
          <div class="partner-invite__cancel-confirm-actions">
            <button type="button" class="account-btn account-btn--ghost"
                    data-action="cancel-invite-back" ${disabled}>Keep it</button>
            <button type="button" class="account-btn account-btn--danger"
                    data-action="cancel-invite-confirm" ${disabled}>
              ${this.cancellingInvite ? 'Cancelling\u2026' : 'Yes, cancel'}
            </button>
          </div>
        </div>
      `;
    }
    return `
      <div class="partner-invite__cancel-footer">
        <button type="button" class="partner-invite__cancel-link"
                data-action="cancel-invite">
          Cancel invitation
        </button>
      </div>
    `;
  }

  _renderFaq() {
    return `
      <div class="faq partner-invite__faq">
        <h3 class="faq__title">Common questions</h3>
        <div class="faq__list">
          ${FAQ_ITEMS.map((item, idx) => `
            <div class="faq__item">
              <button class="faq__question" data-faq="${idx}">
                <span>${escapeHtml(item.q)}</span>
                <span class="faq__icon">+</span>
              </button>
              <div class="faq__answer" data-faq-answer="${idx}">
                ${item.aHtml ? item.aHtml : `<p>${escapeHtml(item.a)}</p>`}
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  /**
   * Page-info tabs for the title's "About Partner" panel. Page-level
   * content, independent of the active top-level state — the icon is
   * available whether the page is showing the invite flow or the linked
   * Overview/Sharing pills, so this covers all of it rather than just
   * whichever state happens to be on screen.
   */
  /** The sharing tab reads the same linked or not (the switch only exists
   * once linked, and the copy already says so), so both stateful tab maps
   * below share this one entry. */
  static PAGE_INFO_SHARING_TAB = {
    label: 'Partner sharing',
    content: `
      <h4 class="page-info-detail__subtitle">Only What You Approve</h4>
      <p class="page-info-detail__text">
        Once you’re linked, the Partner Sharing switch controls what crosses
        over — some discovery progress, approved notes, and select assessment
        results. You can turn it on, off, or adjust it any time.
      </p>
      <div class="partner-privacy">${renderSharedPrivateColumns()}</div>
      ${renderPageInfoFaq([
        {
          q: 'What crosses over without my approval?',
          a: `Nothing. Only what you’ve approved for sharing crosses over,
            and you can adjust it any time.`,
        },
        {
          q: 'Can I change my mind later?',
          a: 'Yes — the switch is yours. Turn sharing off any time.',
        },
      ])}
    `,
  };

  /** Info tabs before the link exists: how linking works, then sharing. */
  static PAGE_INFO_TABS_UNLINKED = {
    linking: {
      label: 'Partner linking',
      content: `
        <h4 class="page-info-detail__subtitle">Two Accounts, One Shared Picture</h4>
        <p class="page-info-detail__text">
          Linking connects encouple to your partner’s account so you’re both
          drawing on the same shared understanding. It doesn’t merge anything —
          each of you keeps your own conversations and progress.
        </p>
        ${renderPageInfoFlow({
          steps: [
            {
              label: 'Send the invitation',
              detail: 'Share a link, or send it by email from this page.',
            },
            {
              label: 'Your partner joins',
              detail: 'They create their own account, with their own private space.',
            },
            {
              label: 'You’re linked',
              detail: `The door opens for what you each choose to share —
                nothing crosses on its own.`,
            },
          ],
        })}
        ${renderPageInfoFaq([
          {
            q: 'Does linking merge our accounts?',
            a: `No. Each of you keeps your own conversations and progress —
              the link only opens the door for what you choose to share.`,
          },
          {
            q: 'How long does the invitation last?',
            a: `It stays open until you cancel it — nothing changes for you
              in the meantime, and you can resend or share it again any time.`,
          },
          {
            q: 'Can my partner see my conversations?',
            a: `No — your conversations stay private. Only what you approve
              for sharing crosses over.`,
          },
        ])}
      `,
    },
    sharing: PartnerSection.PAGE_INFO_SHARING_TAB,
  };

  /** Info tabs once linked: the linking tab becomes managing-the-link. */
  static PAGE_INFO_TABS_LINKED = {
    linking: {
      label: 'Partner linking',
      content: `
        <h4 class="page-info-detail__subtitle">You’re Always In Control</h4>
        <p class="page-info-detail__text">
          You’re linked — and each of you still keeps your own private space
          and progress. The link stays until either of you disconnects it,
          and managing it lives on this page.
        </p>
        ${renderPageInfoFaq([
          {
            q: 'What happens if we disconnect?',
            a: `You both keep your own individual data. Shared progress stops
              updating right away, and it won’t carry back over if you
              reconnect.`,
          },
          {
            q: 'Does my partner see my conversations?',
            a: `No — your conversations stay private. Only what you approve
              for sharing crosses over.`,
          },
          {
            q: 'Can we relink later?',
            a: `Yes — you can reconnect any time, though shared progress from
              before won’t carry back over.`,
          },
        ])}
      `,
    },
    sharing: PartnerSection.PAGE_INFO_SHARING_TAB,
  };

  /**
   * Open the "About Partner" panel. Stateful: the linking tab explains how to
   * link while unlinked, and shifts to managing-the-link once linked.
   */
  showPageInfo() {
    const content = buildPageInfoPanel({
      title: 'About Partner',
      intro: `Partner connects your account to your partner’s, so encouple
        can understand you as a couple as well as individually — you
        control what crosses over.`,
      tabs: this.state === 'linked'
        ? PartnerSection.PAGE_INFO_TABS_LINKED
        : PartnerSection.PAGE_INFO_TABS_UNLINKED
    });

    eventBus.emit('right-panel:open', {
      content,
      // MUST end in `-info`: PageManager keys off that suffix to let the
      // active section clear its routed detail before the panel swaps.
      contentType: 'partner-info'
    });
  }

  /* -------------------------------------------------------------------- *
   * Cancelled state (post-revoke)                                        *
   * -------------------------------------------------------------------- */

  _renderCancelledSections() {
    return `
      <div class="partner-invite">
        <div class="card account-section__card partner-invite__card">
          <div class="account-row">
            <div class="account-row__body">
              <span class="account-row__label">Invitation cancelled</span>
              <span class="account-row__value">
                The previous link no longer works.
              </span>
            </div>
            <div class="account-row__action">
              <button type="button" class="account-btn account-btn--primary"
                      data-action="recreate-invitation"
                      ${this.recreating ? 'disabled aria-busy="true"' : ''}>
                ${this.recreating ? 'Creating\u2026' : 'Create a new invitation'}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Mirrors router.js's `isOrientationInProgress()` and app.js's
   * `needsOrientation()` — kept inline to match the existing pattern
   * (both source files do the same to avoid circular imports).
   */
  _isInOrientation() {
    const app = window.app;
    if (!app) return false;
    if (app.modules?.modeOrchestrator?.getCurrentMode() === 'ORIENTATION') {
      return true;
    }
    if (app.currentUser?.onboardingStep && app.currentUser.onboardingStep !== 'complete') {
      return true;
    }
    return false;
  }

  /* -------------------------------------------------------------------- *
   * Event delegation                                                     *
   * -------------------------------------------------------------------- */

  handleClick(event) {
    // FAQ toggles are inside the partner page but use a `data-faq` attr,
    // not `data-action`. Handle them first so the action-switch below
    // doesn't have to know about them.
    const faqQuestion = event.target.closest('.faq__question');
    if (faqQuestion && this.container.contains(faqQuestion)) {
      this._toggleFaq(faqQuestion);
      return;
    }

    const target = event.target.closest('[data-action]');
    if (!target || !this.container.contains(target)) return;
    const action = target.getAttribute('data-action');
    if (!action) return;

    switch (action) {
      // Linked-state — Overview/Sharing pill nav
      case 'partner-tab': {
        event.preventDefault();
        const tab = target.getAttribute('data-tab') === 'sharing' ? 'sharing' : 'overview';
        this._switchTab(tab);
        break;
      }

      // Linked-state — disconnect flow
      case 'disconnect':
        event.preventDefault();
        this.confirmingDisconnect = true;
        this.disconnectConfirmText = '';
        this.errorMessage = '';
        this.rerender({ focus: 'disconnect-input' });
        break;
      case 'cancel-disconnect':
        event.preventDefault();
        if (this.disconnecting) return;
        this.confirmingDisconnect = false;
        this.disconnectConfirmText = '';
        this.rerender();
        break;
      case 'confirm-disconnect':
        event.preventDefault();
        if (this.disconnectConfirmText !== DISCONNECT_TOKEN) return;
        this._performDisconnect();
        break;

      // Unlinked-state — share / copy / channel switch
      case 'invite-copy':
        // Fires from the Copy button AND from a click anywhere in the link
        // field (the field carries the same data-action). If the user has
        // actually selected part of the URL by dragging, leave them alone —
        // the mouseup at the end of a drag also lands here, and replacing a
        // deliberate partial selection with a whole-URL copy would fight them.
        if (this._hasLinkSelection()) return;
        event.preventDefault();
        this._handleCopy();
        break;
      case 'invite-share':
        event.preventDefault();
        this._handleShare();
        break;
      // One case for both directions: the segmented control is a switch, not
      // an open/cancel pair, so there is no asymmetric "abandon" path.
      case 'invite-channel': {
        event.preventDefault();
        // A send is in flight against the email field — swapping it out from
        // under the request would strand the pending state with no surface to
        // report back to.
        if (this.sendingEmail) return;
        const channel = target.getAttribute('data-channel') === 'email' ? 'email' : 'link';
        if (channel === this.inviteChannel) return;
        this.inviteChannel = channel;
        this.emailError = '';
        this.rerender(channel === 'email' ? { focus: 'email-input' } : undefined);
        break;
      }
      case 'invite-email-submit':
        event.preventDefault();
        this._submitEmail();
        break;

      // Unlinked-state — cancel invitation flow
      case 'cancel-invite':
        event.preventDefault();
        this.confirmingCancelInvite = true;
        this.rerender();
        break;
      case 'cancel-invite-back':
        event.preventDefault();
        if (this.cancellingInvite) return;
        this.confirmingCancelInvite = false;
        this.rerender();
        break;
      case 'cancel-invite-confirm':
        event.preventDefault();
        this._performCancelInvitation();
        break;

      // Cancelled-state — recreate
      case 'recreate-invitation':
        event.preventDefault();
        this._recreateInvitation();
        break;

      // Orientation banner — back to setup (chat in orientation mode).
      // handleBaseRoute Priority 0 keeps the user on chat with orientation
      // mode intact (verified in router.js:254-261).
      case 'orientation-return':
        event.preventDefault();
        eventBus.emit('navigate', { page: 'chat' });
        break;

      // Error retry
      case 'retry':
        event.preventDefault();
        this.activate();
        break;

      // Foundations gate → the path that clears it. Enters the discovery chat
      // session directly, same as the dashboard's Recommended Activities CTA
      // and the /discovery path detail page's "Continue this path" button.
      case 'gate-continue': {
        event.preventDefault();
        const pathId = target.getAttribute('data-path-id');
        const pathName = target.getAttribute('data-path-name');
        if (pathId) {
          eventBus.emit('navigate', {
            page: 'chat',
            mode: 'DISCOVERY',
            pathId,
            pathMetadata: { id: pathId, name: pathName }
          });
        }
        break;
      }

      // ----- Sharing card (partner-gestalt consent) -----
      case 'sharing-retry':
        event.preventDefault();
        this.sharingLoadStatus = 'idle';
        this._loadSharingStatus();
        break;
      case 'sharing-understanding':
        event.preventDefault();
        this._toggleUnderstanding();
        break;
      case 'sharing-memo-feed-notice-dismiss':
        event.preventDefault();
        this._dismissMemoFeedNotice();
        break;

      // ----- Partner-rate upsell banner -----
      case 'partner-rate-upsell-view':
        event.preventDefault();
        goToUpgrade(this.eventBus || eventBus, 'partnerUpsell');
        break;
      case 'partner-rate-upsell-dismiss':
        event.preventDefault();
        this._dismissPartnerRateUpsell();
        break;

      default:
    }
  }

  /**
   * Open/close one FAQ accordion item. Mirrors UpgradeSection.handleFaqToggle:
   * toggles `.faq__item--open` and swaps the icon glyph (+ ↔ −). Doesn't
   * re-render the page (avoids losing other items' open state).
   */
  _toggleFaq(question) {
    const item = question.closest('.faq__item');
    const icon = question.querySelector('.faq__icon');
    if (!item) return;
    const isOpen = item.classList.toggle('faq__item--open');
    if (icon) icon.textContent = isOpen ? '\u2212' : '+';
  }

  handleSubmit(event) {
    const form = event.target.closest('form[data-form]');
    if (!form) return;
    event.preventDefault();
    if (form.dataset.form === 'invite-email') {
      this._submitEmail();
    }
  }

  handleInput(event) {
    const target = event.target;
    if (!target) return;

    if (target.id === 'partner-disconnect-input') {
      this.disconnectConfirmText = target.value;
      const btn = this.container.querySelector('#partner-disconnect-confirm');
      if (btn) btn.disabled = (this.disconnectConfirmText !== DISCONNECT_TOKEN) || this.disconnecting;
      return;
    }

    if (target.id === 'partner-email-input') {
      // Clear inline error once the user starts editing
      if (this.emailError) {
        this.emailError = '';
        const errEl = this.container.querySelector('.account-form__error');
        if (errEl) errEl.remove();
      }
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      if (this.confirmingDisconnect && !this.disconnecting) {
        this.confirmingDisconnect = false;
        this.disconnectConfirmText = '';
        this.rerender();
        return;
      }
      if (this.confirmingCancelInvite && !this.cancellingInvite) {
        this.confirmingCancelInvite = false;
        this.rerender();
        return;
      }
      // Escape backs out of the email channel to the link channel — the
      // channel switch is a mode, and Escape is how you leave a mode.
      if (this.inviteChannel === 'email' && !this.sendingEmail) {
        this.inviteChannel = 'link';
        this.emailError = '';
        this.rerender();
        return;
      }
      return;
    }
    if (event.key === 'Enter'
        && event.target?.id === 'partner-disconnect-input'
        && !this.disconnecting
        && this.disconnectConfirmText === DISCONNECT_TOKEN) {
      event.preventDefault();
      this._performDisconnect();
      return;
    }
  }

  /* -------------------------------------------------------------------- *
   * Linked-state actions                                                 *
   * -------------------------------------------------------------------- */

  async _performDisconnect() {
    // Mock preview: never dissolve the real couple link from a fixture.
    if (this._mockActive()) {
      window.notificationManager?.showSuccess('(mock) Disconnect isn’t performed in preview.');
      this.confirmingDisconnect = false;
      this.disconnectConfirmText = '';
      this.render();
      return;
    }
    this.disconnecting = true;
    this.rerender();
    try {
      const result = await this.discoveryApi.dissolveCouple();
      if (!this.isActive) return;
      if (result?.success === false) {
        this.errorMessage = result?.error || 'Could not disconnect right now. Please try again.';
        this.confirmingDisconnect = false;
        return;
      }
      eventBus.emit('partner:unlinked');
      // Disconnecting ends the partner rate for both parties — stale-mark the
      // shared snapshot so the /subscription partner-rate row, the chip here
      // and every other reader re-read truth instead of the pre-disconnect
      // payload.
      invalidateSubscriptionStatus();
      window.notificationManager?.showSuccess('Partner disconnected.');
      this.partner = null;
      this._resetTransientState();
      // After unlink, transition straight into the active-invite flow so
      // the user has somewhere to go (re-invite or close).
      this.state = 'loading';
      this.render();
      await this._loadInvitation();
    } catch (err) {
      if (!this.isActive) return;
      console.error('[PartnerSection] Disconnect failed:', err?.error || err);
      this.errorMessage = err?.error || 'Could not disconnect right now. Please try again.';
      this.confirmingDisconnect = false;
    } finally {
      this.disconnecting = false;
      if (this.isActive && this.state === 'linked') this.rerender();
    }
  }

  /* -------------------------------------------------------------------- *
   * Unlinked-state actions                                               *
   * -------------------------------------------------------------------- */

  /**
   * True when the user has a live partial selection inside the link field.
   * A full selection (or none) doesn't count — selecting everything wants the
   * same bytes the copy would produce, so there's nothing to preserve.
   * @returns {boolean}
   */
  _hasLinkSelection() {
    const input = this.container.querySelector('#partner-invite-link');
    if (!(input instanceof HTMLInputElement)) return false;
    const { selectionStart, selectionEnd, value } = input;
    if (selectionStart === null || selectionEnd === null) return false;
    const length = selectionEnd - selectionStart;
    return length > 0 && length < value.length;
  }

  async _handleCopy() {
    const url = this.invitation?.url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      this._setCopiedFlash();
    } catch (_err) {
      const input = this.container.querySelector('#partner-invite-link');
      if (input instanceof HTMLInputElement) {
        input.focus();
        input.select();
      }
    }
  }

  _setCopiedFlash() {
    this._clearCopyTimer();
    this.copied = true;
    this.rerender();
    this._copyRevertTimer = setTimeout(() => {
      if (!this.isActive || !this.copied) return;
      this.copied = false;
      this.rerender();
    }, 2000);
  }

  async _handleShare() {
    if (typeof navigator.share !== 'function') return;
    try {
      await navigator.share({
        title: 'Join me on encouple',
        text: 'Here\u2019s the invitation to join me on encouple',
        url: this.invitation?.url
      });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      // Fall back to the copy path so they still get the link out somehow.
      this._handleCopy();
    }
  }

  async _submitEmail() {
    const input = this.container.querySelector('#partner-email-input');
    const email = (input instanceof HTMLInputElement ? input.value : '').trim();
    if (!EMAIL_REGEX.test(email)) {
      this.emailError = 'Please enter a valid email address.';
      this.rerender({ focus: 'email-input' });
      return;
    }
    if (!this.invitation?.id) {
      this.emailError = 'Your invitation link isn\u2019t ready yet. Try again in a moment.';
      this.rerender();
      return;
    }

    this.sendingEmail = true;
    this.emailError = '';
    this.rerender();

    try {
      await this.discoveryApi.sendInvitationEmail(this.invitation.id, email);
      if (!this.isActive) return;
      // Reflect the persisted invitee email on the local copy so the
      // re-render shows "Last sent to X" without a re-fetch.
      this.invitation = {
        ...this.invitation,
        inviteeEmail: email,
        inviteeEmailSentAt: new Date().toISOString()
      };
      // STAY on the email channel — the confirmation now renders here, in this
      // channel's helper slot ("Last sent to X on DATE") with the submit button
      // becoming "Resend". Bouncing back to the link channel would land the
      // user somewhere that says nothing about what just happened.
      window.notificationManager?.showSuccess(`Invitation sent to ${email}.`);
    } catch (err) {
      if (!this.isActive) return;
      console.error('[PartnerSection] Send email failed:', err?.error || err);
      this.emailError = err?.error || err?.message || 'Could not send the email. Please try again.';
    } finally {
      this.sendingEmail = false;
      if (this.isActive) this.rerender();
    }
  }

  async _performCancelInvitation() {
    this.cancellingInvite = true;
    this.rerender();
    try {
      await this.discoveryApi.revokeInvitation();
      if (!this.isActive) return;
      this.invitation = null;
      this._resetTransientState();
      this.state = 'cancelled';
    } catch (err) {
      if (!this.isActive) return;
      console.error('[PartnerSection] Revoke failed:', err?.error || err);
      this.errorMessage = err?.error || 'Could not cancel the invitation. Please try again.';
      this.confirmingCancelInvite = false;
    } finally {
      this.cancellingInvite = false;
      if (this.isActive) this.rerender();
    }
  }

  async _recreateInvitation() {
    this.recreating = true;
    this.rerender();
    await this._loadInvitation();
    // _loadInvitation handles success (state = 'active') and error path; the
    // recreating flag is cleared inside _loadInvitation on success and via
    // the catch-branch render. Safety reset:
    if (this.isActive && this.recreating) {
      this.recreating = false;
      this.rerender();
    }
  }

  /* -------------------------------------------------------------------- *
   * Sharing — data loading, polling, and consent actions.                 *
   * -------------------------------------------------------------------- */

  /**
   * Fetch the Sharing view model. Called once when the linked state is
   * established (the Overview masters need it too) and again as a light poll
   * while the registers are `generating`. Degrades to an `unavailable` status
   * (never an error) on a 404 (no active couple).
   *
   * Inert under the mock harness: the fixture IS the view model there, and a
   * real read landing on top of it would silently discard the fixture's ledger
   * mid-walkthrough.
   *
   * @param {Object} [opts]
   * @param {boolean} [opts.poll=false] - true for background poll re-fetches
   *   (suppresses the interim loading render).
   */
  async _loadSharingStatus({ poll = false } = {}) {
    if (this.state !== 'linked') return; // only meaningful with a couple
    if (this._mockActive()) return;
    if (!poll) {
      this.sharingLoadStatus = 'loading';
      this.sharingError = '';
      if (this.isActive) this.render();
    }
    try {
      const res = await this.discoveryApi.getSharingStatus();
      if (!this.isActive) return;
      this.sharing = res?.data || null;
      this.sharingLoadStatus = 'loaded';
      this._syncRegistryLedger();
    } catch (err) {
      if (!this.isActive) return;
      if (err?.status === 404) {
        // No active couple (e.g. dissolved race) — not an error, just unavailable.
        this.sharing = null;
        this.registryLedger = null;
        this.sharingLoadStatus = 'unavailable';
      } else {
        this.sharingError = err?.error || 'Could not load your sharing settings.';
        this.sharingLoadStatus = 'error';
      }
    } finally {
      if (this.isActive) {
        this._syncSharingPoll();
        // A background poll tick must never re-render under an open consent
        // run: `render()` destroys and rebuilds the registry, and the staged
        // batch — held only in the detail panel — would go with it, silently,
        // mid-review. The ledger merge above has already landed in place (by
        // row identity), so the run keeps working against fresh data; the
        // paint waits for a later tick, or for the close that repaints the
        // rows itself. A non-poll load always renders, as it always has.
        if (!poll || !this._sharingRegistry?.hasActiveRun()) this.render();
        // Second consumption site for the `?share=1` intent: on a cold nav the
        // router's applyURLParams call lands while this fetch is still in
        // flight (activate() doesn't await it), so the scroll fires from the
        // post-load render. One-shot — the flag clears on consumption, so the
        // bounded generating-poll refetch can't re-trigger it.
        if (this._scrollToSharing) this._consumeScrollIntent();
        // Same two-site pattern for the `?queue=<groupId>` intent — the
        // render() above (when it runs) is what mounts the registry, so the
        // openable check below sees a live `_sharingRegistry` when there is one.
        if (this._queueIntent) this._consumeQueueIntent();
      }
    }
  }

  /**
   * Start/stop the poll based on the current view — see SHARING_POLL_STATES for
   * which states self-resolve and why. Bounded, per state.
   */
  _syncSharingPoll() {
    this._clearSharingPoll();
    const state = this.sharingLoadStatus === 'loaded' ? this.sharing?.state : null;
    if (!SHARING_POLL_STATES.has(state)) {
      this._sharingPollState = null;
      this._sharingPollAttempts = 0;
      return;
    }
    // Each polled state gets its own budget. `generating` waits on a build and
    // `waiting` waits on the recovery read — two separate windows, so a long
    // build must not spend the attempts the confirm-race recovery then needs.
    if (state !== this._sharingPollState) {
      this._sharingPollState = state;
      this._sharingPollAttempts = 0;
    }
    if (this._sharingPollAttempts >= SHARING_POLL_MAX) return;
    this._sharingPollTimer = setTimeout(() => {
      this._sharingPollTimer = null;
      if (!this.isActive) return;
      this._sharingPollAttempts += 1;
      this._loadSharingStatus({ poll: true });
    }, SHARING_POLL_INTERVAL_MS);
  }

  _clearSharingPoll() {
    if (this._sharingPollTimer) {
      clearTimeout(this._sharingPollTimer);
      this._sharingPollTimer = null;
    }
  }

  /**
   * Rebuild the registry's ledger from the current sharing-status payload,
   * IN PLACE — `SharingRegistry` holds `registryLedger` by reference, so a
   * fresh object here would strand a mounted registry on a stale ledger.
   *
   * Unit rows are merged BY ID onto the objects already in the ledger rather
   * than swapped wholesale: the detail panel writes and reads a row it captured
   * before the round trip (that capture is what it restores on a rejection), and
   * a replacement array would leave it holding a row no longer in the ledger.
   * The server sends every field of a row it sends at all, so the merge is a
   * full overwrite, not a patch.
   */
  _syncRegistryLedger() {
    const next = this._ledgerFromStatus(this.sharing);
    if (!next || !this.registryLedger) {
      this.registryLedger = next;
      return;
    }
    const live = new Map(this.registryLedger.units.map(unit => [unit.id, unit]));
    this.registryLedger.masters = next.masters;
    this.registryLedger.groups = next.groups;
    this.registryLedger.units = next.units.map(unit => {
      const held = live.get(unit.id);
      return held ? Object.assign(held, unit) : unit;
    });
  }

  /**
   * The registry's ledger view of a sharing-status payload, or null when there
   * is nothing to consent to yet (locked / generating / error all arrive with
   * no share units — the Sharing pill shows their reason instead).
   *
   * The mapping itself lives in `partnerSharing/sharingLedgerModel.js`: the
   * in-chat ceremony launcher mounts the same panel off the same payload, so
   * one host may not hold a private copy of the view→ledger contract.
   *
   * @param {Object|null} view - getSharingStatus payload
   * @returns {Object|null} `{masters, groups, units}`
   */
  _ledgerFromStatus(view) {
    return ledgerFromStatus(view);
  }

  /**
   * The registry's per-unit consent seam.
   *
   * Every sharing mutation answers with the WHOLE view model, this one included
   * — so the response is re-synced exactly like confirm/master, and the unit the
   * registry asked about is then extracted from it. Extraction is not optional:
   * the view's top-level `state` is the page's 7-state string (`ready`/`live`/…),
   * and handing that back as a unit row would write a page state into a consent
   * field. The requested value is the fallback only for a response that carries
   * no row for this unit at all.
   *
   * @param {string} unitId
   * @param {'full'|'basic'|'off'} state
   * @returns {Promise<Object|null>} The canonical unit row, or null
   */
  async _commitSharingUnit(unitId, state) {
    const res = await this.discoveryApi.setSharingUnit(unitId, state);
    const view = res?.data;
    if (!view || !Array.isArray(view.units)) return null;
    this.sharing = view;
    this._syncRegistryLedger();
    return view.units.find(u => u.id === unitId) || null;
  }

  /**
   * The registry's batch-submit seam — the consent queue's one write. The
   * response is the whole view model (submitting can flip the couple live,
   * which changes the masters block, the partner's confirm, and every unit's
   * consumers at once), so the Overview card and the ledger both re-sync from
   * it, and the poll restarts: submit is precisely the entry into
   * `waiting`/`generating`, whose recovery read the poll drives.
   *
   * A response that carries no `units` array is a REJECTION, not a quiet no-op:
   * the panel's success path closes itself and animates the rows, and neither
   * may happen on a payload that never proved the batch landed. That guard is
   * `ledgerFromSubmitResponse` — shared with the in-chat ceremony host, which
   * submits through the same seam.
   *
   * @param {Record<string, 'full'|'basic'|'off'>} unitStates
   * @returns {Promise<void>}
   * @throws {{error: string}} When the response carries no unit ledger
   */
  async _commitSharingSubmit(unitStates) {
    const res = await this.discoveryApi.submitSharing(unitStates);
    const view = ledgerFromSubmitResponse(res);
    this.sharing = view;
    this._syncRegistryLedger();
    this._syncSharingPoll();
  }

  /* ----- The Partner Sharing master switch (Overview) ----- */

  /**
   * The single Partner Sharing switch — the discovery master, and the sole
   * pause gate: there is no separate revoke. OFF pauses ALL sharing (the
   * notes→partner-memory feed and assessment sharing alike), tears the shared
   * understanding down and clears this user's confirm while RETAINING every
   * unit's state (gates, not erasers); ON re-opens the registry for a fresh
   * "Approve & share" confirm on the overview — the notes feed resumes
   * immediately.
   *
   * The response is the whole view model, so the ledger is re-synced from it
   * too. Fires in every gestalt state — locked/generating/error no longer
   * disable the switch (the notes half has no Foundations gate); the backend
   * master PATCH is safe in all of them.
   */
  async _toggleUnderstanding() {
    if (this._sharingBusy || this._sharingSubmitting || !this.sharing) return;
    const cur = this.sharing.masters?.discovery !== false;
    const next = !cur;
    if (this._mockActive()) {
      this.sharing.masters = { ...(this.sharing.masters || {}), discovery: next };
      this.render();
      return;
    }
    this._sharingSubmitting = true;
    this.render();
    try {
      const res = await this.discoveryApi.setDiscoveryMaster(next);
      if (!this.isActive) return;
      if (res?.data) {
        this.sharing = res.data;
        this._syncRegistryLedger();
      }
    } catch (err) {
      if (!this.isActive) return;
      if (err?.status === 404) {
        // Couple dissolved mid-toggle — land on the correct state, not a toast.
        this._loadSharingStatus();
        return;
      }
      window.notificationManager?.showError(err?.error || 'Could not update sharing. Please try again.');
    } finally {
      this._sharingSubmitting = false;
      if (this.isActive) {
        this._syncSharingPoll();
        this.render();
      }
    }
  }

  /* ----- W9 first-feed notice dismiss ----- */

  async _dismissMemoFeedNotice() {
    // Also barred while the master PATCH is in flight: its response replaces
    // `this.sharing` wholesale, which would silently revert an optimistic
    // dismiss committed after the master's server read.
    if (this._sharingBusy || this._sharingSubmitting || !this.sharing) return;
    if (this._mockActive()) {
      this.sharing.firstFeedNoticePending = false;
      this.render();
      return;
    }
    this._sharingBusy = true;
    this.sharing.firstFeedNoticePending = false; // optimistic
    this.render();
    try {
      await this.discoveryApi.dismissMemoFeedNotice();
    } catch (err) {
      if (!this.isActive) return;
      this.sharing.firstFeedNoticePending = true; // revert
      window.notificationManager?.showError(err?.error || 'Could not dismiss that. Please try again.');
    } finally {
      this._sharingBusy = false;
      if (this.isActive) this.render();
    }
  }
}

export default PartnerSection;
